import React, { useState } from "react";
import { api, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { Check, Truck, Package, Trash2 } from "lucide-react";
import { Tile } from "./AdminTable";

export function RewardDetail({ claim, statusCfg, onClose }) {
    const [tracking, setTracking] = useState(claim.tracking_number || "");
    const [address, setAddress] = useState(claim.shipping_address || "");
    const [notes, setNotes] = useState(claim.notes || "");
    const [saving, setSaving] = useState(false);

    const update = async (newStatus) => {
        if (newStatus === "shipped" && !tracking.trim()) {
            toast.error("Numéro de suivi requis avant expédition");
            return;
        }
        setSaving(true);
        try {
            await api.post(
                `/admin/rewards/${claim.id}/status?status=${newStatus}`,
                { tracking_number: tracking, shipping_address: address, notes }
            );
            toast.success("Récompense mise à jour");
            onClose();
        } catch (e) {
            toast.error(formatErr(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const s = statusCfg[claim.status] || { cls: "", l: claim.status };

    return (
        <div className="space-y-6">
            <button onClick={onClose} className="text-xs uppercase tracking-widest text-zinc-400 hover:text-orange-500">
                ← Retour
            </button>
            <div className="card-tu space-y-6">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="text-[11px] uppercase tracking-[0.25em] text-orange-500 font-bold mb-1">
                            Récompense fidélité
                        </div>
                        <h2 className="font-display text-4xl uppercase tracking-tight">
                            {claim.club_name}
                        </h2>
                        <div className="text-zinc-400 text-sm mt-1">
                            {claim.club_city} · {claim.club_department}
                        </div>
                    </div>
                    <span className={`chip ${s.cls}`}>{s.l}</span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
                    <Tile label="Palier" value={`${claim.threshold} pts`} />
                    <Tile label="Récompense" value={claim.reward_label} />
                    <Tile label="Créée le" value={(claim.created_at || "").slice(0, 10)} />
                    <Tile
                        label="Expédiée le"
                        value={claim.shipped_at ? claim.shipped_at.slice(0, 10) : "—"}
                    />
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="label-tu">Adresse de livraison</label>
                        <textarea
                            data-testid="reward-address"
                            rows={2}
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="Adresse complète du club"
                            className="input-tu"
                        />
                    </div>
                    <div>
                        <label className="label-tu">Numéro de suivi (transporteur)</label>
                        <input
                            data-testid="reward-tracking"
                            value={tracking}
                            onChange={(e) => setTracking(e.target.value)}
                            placeholder="Ex. 1Z999AA10123456784"
                            className="input-tu"
                        />
                    </div>
                    <div>
                        <label className="label-tu">Notes internes</label>
                        <textarea
                            rows={2}
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            className="input-tu"
                        />
                    </div>
                </div>

                <div className="flex flex-wrap gap-2">
                    {claim.status === "pending" && (
                        <button
                            data-testid="reward-prepare"
                            disabled={saving}
                            onClick={() => update("preparing")}
                            className="px-4 py-2 bg-yellow-700 hover:bg-yellow-600 text-white text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1"
                        >
                            <Package size={14} /> Marquer en préparation
                        </button>
                    )}
                    {(claim.status === "pending" || claim.status === "preparing") && (
                        <button
                            data-testid="reward-ship"
                            disabled={saving}
                            onClick={() => update("shipped")}
                            className="btn-primary py-2 px-4 text-xs"
                        >
                            <Truck size={14} /> Marquer expédié
                        </button>
                    )}
                    {claim.status === "shipped" && (
                        <button
                            data-testid="reward-deliver"
                            disabled={saving}
                            onClick={() => update("delivered")}
                            className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1"
                        >
                            <Check size={14} /> Marquer livré
                        </button>
                    )}
                    <button
                        data-testid="reward-save"
                        disabled={saving}
                        onClick={() => update(claim.status)}
                        className="btn-ghost py-2 px-4 text-xs"
                    >
                        Enregistrer
                    </button>
                    {claim.status !== "cancelled" && claim.status !== "delivered" && (
                        <button
                            disabled={saving}
                            onClick={() => update("cancelled")}
                            className="px-4 py-2 border border-red-900 hover:bg-red-950 text-red-400 text-xs uppercase tracking-widest font-bold ml-auto"
                        >
                            Annuler
                        </button>
                    )}
                    <button
                        data-testid="reward-delete"
                        disabled={saving}
                        onClick={async () => {
                            if (!window.confirm("Supprimer définitivement cette récompense ?")) return;
                            try {
                                await api.delete(`/admin/rewards/${claim.id}`);
                                toast.success("Supprimée");
                                onClose();
                            } catch (e) {
                                toast.error(formatErr(e.response?.data?.detail));
                            }
                        }}
                        className="px-4 py-2 border border-red-900 hover:bg-red-950 text-red-400 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1"
                    >
                        <Trash2 size={14} /> Supprimer
                    </button>
                </div>
            </div>
        </div>
    );
}
